/**
 * "Does this sign-in go through the /auth door?", in one place.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: this was two expressions, one in
 * `useSignInOut`'s `signIn` callback and one in `useAuthNavigation`'s auto-guard,
 * differing by a single term, with a `CHANGE ONE, CHANGE THE OTHER` note over
 * each. That note is the durable form of the problem rather than a fix for it,
 * and the reason not to extract did not survive contact: the concern was that a
 * shared helper would live in `useSignInOut.ts` and drag its api-client module
 * graph into `useAuthNavigation`'s load path (which really did break that hook's
 * own suite). But the predicate is made of `featureFlags` and `isNativePlatform`,
 * and both files already import both directly, so a LEAF module beside them
 * pulls in nothing either file does not already have. That is what this is: two
 * imports, no react, no api-client.
 *
 * The consequence of drift is what makes it worth closing rather than noting.
 * Nothing crashes: the two paths simply disagree about whether a given sign-in
 * goes through the door, and since the door is what refreshes the cross-tab
 * mirrors (`AuthDoor`'s mount effect), a divergence surfaces as intents and
 * destinations silently not surviving the tab hop -- indistinguishable from the
 * mirror bugs it would be blamed on.
 *
 * NEVER call this for `useInvalidGrantHandler`'s silent-recovery sign-ins
 * (`Prompt.None` at runWebAttempts' "Attempt 1", the full-login fallback, the
 * native attempt). Those exist to refresh an existing session with no user
 * interaction, and routing them through the door would break token-refresh
 * recovery entirely. That hook calls `logtoSignIn` directly and is deliberately
 * untouched by this flag.
 */
import { featureFlags } from '@config/featureFlags';
import { isNativePlatform } from '@lib/platform';

interface DoorRoutingOptions {
  /**
   * The door's OWN sign-in calls (its four social chips, "Use a code instead",
   * the flag-off email submit) pass `true`: they are already on `/auth`, and
   * routing them back to it is a silent no-op with no escape.
   *
   * The exemption is `bypassDoor`, NOT `directSignIn`. Gating on `directSignIn`
   * alone left "Use a code instead" (loginHint, no directSignIn) routed back to
   * the door, stranding a visitor whose magic link never arrived.
   *
   * `useAuthNavigation`'s auto-guard takes no options at all, so it simply omits
   * this -- the term has nothing to read there, which is why the two expressions
   * legitimately differed by it, and why the default is `false` rather than the
   * caller being made to pass something.
   */
  bypassDoor?: boolean;
}

export const shouldRouteToDoor = (options: DoorRoutingOptions = {}): boolean =>
  featureFlags.magicLinkAuth && !options.bypassDoor && !isNativePlatform();
